import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import { useNotifications } from "../contexts/NotificationContext";
import {
  Home,
  Search,
  PlusCircle,
  MessageCircle,
  User,
  Heart,
  Map,
  Menu,
  X,
  Settings,
  LogOut,
  BarChart3,
  Award,
  Users,
  Bell,
  TrendingUp,
  Scale,
  Gift,
  Globe,
  Bookmark,
  Shield,
  ChevronRight,
  Gavel,
  Code2,
  Crown,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import DonarisPromoBar from "./DonarisPromoBar";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UnifiedNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();
  const { language, setLanguage, languages } = useLanguage();

  const [showMenu, setShowMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [topVisible, setTopVisible] = useState(true);
  const lastScrollY = useRef(0);

  const hiddenPaths = ["/auth", "/admin", "/create-ad"];
  const shouldHide = hiddenPaths.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (!showSearch) return;
    const onKey = (e) => {
      if (e.key === "Escape") setShowSearch(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showSearch]);

  const totalUnread = unreadMessages + (unreadCount || 0);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  // Auto-hide top pill on scroll-down, show on scroll-up
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y < 80) {
        setTopVisible(true);
      } else if (y > lastScrollY.current + 6) {
        setTopVisible(false);
      } else if (y < lastScrollY.current - 6) {
        setTopVisible(true);
      }
      lastScrollY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Fetch unread messages count when user present
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/messages/unread-count`, {
          withCredentials: true,
        });
        if (!cancelled) setUnreadMessages(res.data?.count || 0);
      } catch {
        /* ignore */
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [user]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/category/all?search=${encodeURIComponent(searchQuery)}`);
      setShowSearch(false);
      setSearchQuery("");
    }
  };

  const handleLogout = async () => {
    await logout();
    setShowMenu(false);
    navigate("/");
  };

  if (shouldHide) return null;

  return (
    <>
      {/* ============ TOP FLOATING PILL ============ */}
      <div
        className={`fixed top-0 left-0 right-0 z-[70] pointer-events-none transition-transform duration-300 pt-safe ${
          topVisible ? "translate-y-0" : "-translate-y-full"
        }`}
        data-testid="unified-top-pill"
      >
        <DonarisPromoBar />
        <div className="pt-3 sm:pt-4 px-3 sm:px-6 pointer-events-auto">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-2 sm:gap-3 h-12 sm:h-14 px-2 sm:px-3 rounded-full bg-[#0a0a0a]/80 backdrop-blur-xl border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.5)]">
              {/* Logo */}
              <Link
                to="/"
                className="flex items-center gap-1.5 pl-2 pr-3 h-full flex-shrink-0 group"
                data-testid="nav-logo"
                aria-label="X67 Home"
              >
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:shadow-blue-500/60 transition-shadow">
                  <span className="text-white font-black text-xs sm:text-sm tracking-tight">X67</span>
                </div>
                <span className="hidden sm:block text-white font-semibold text-sm tracking-tight">
                  Digital
                </span>
              </Link>

              {/* Search - clickable "input-like" button */}
              <button
                type="button"
                onClick={() => setShowSearch(true)}
                className="flex-1 flex items-center gap-2 px-3 h-8 sm:h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/15 transition-all group min-w-0"
                data-testid="nav-search-trigger"
              >
                <Search className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 group-hover:text-white transition-colors flex-shrink-0" />
                <span className="text-slate-500 group-hover:text-slate-300 transition-colors text-xs sm:text-sm truncate">
                  Caută anunțuri...
                </span>
                <span className="ml-auto hidden sm:flex items-center gap-1 text-[10px] text-slate-600 bg-white/5 rounded-md px-1.5 py-0.5 border border-white/5 flex-shrink-0">
                  <kbd className="font-mono">⌘</kbd>
                  <kbd className="font-mono">K</kbd>
                </span>
              </button>

              {/* Messages quick-access (desktop only) */}
              <Link
                to={user ? "/messages" : "/auth"}
                className="relative hidden sm:flex items-center justify-center w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/5 transition-all"
                data-testid="nav-messages-top"
                aria-label="Mesaje"
              >
                <MessageCircle className="w-4 h-4 text-slate-300" />
                {totalUnread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                    {totalUnread > 9 ? "9+" : totalUnread}
                  </span>
                )}
              </Link>

              {/* Avatar / Sign-in */}
              {user ? (
                <button
                  onClick={() => setShowMenu(true)}
                  className="flex-shrink-0 relative"
                  data-testid="nav-avatar-btn"
                  aria-label="Meniu utilizator"
                >
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name || "Avatar utilizator"}
                      className="w-8 h-8 sm:w-9 sm:h-9 rounded-full object-cover border-2 border-white/10 hover:border-blue-500/60 transition-colors"
                    />
                  ) : (
                    <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center border-2 border-white/10 hover:border-white/30 transition-colors">
                      <span className="text-white text-xs sm:text-sm font-bold">
                        {user.name?.charAt(0).toUpperCase() || "U"}
                      </span>
                    </div>
                  )}
                  {user.role === "admin" && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-amber-400 border-2 border-[#0a0a0a]" title="Admin" />
                  )}
                  {user.is_verified && user.role !== "admin" && (
                    <span
                      className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 border-2 border-[#0a0a0a] flex items-center justify-center shadow-[0_0_6px_rgba(16,185,129,0.8)]"
                      title="Cont verificat"
                      data-testid="nav-verified-dot"
                    >
                      <svg viewBox="0 0 24 24" className="w-2 h-2 text-white" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </span>
                  )}
                </button>
              ) : (
                <Link
                  to="/auth"
                  className="flex-shrink-0 px-3 sm:px-4 h-8 sm:h-9 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-semibold text-xs sm:text-sm flex items-center shadow-lg shadow-blue-500/30 transition-all"
                  data-testid="nav-auth-btn"
                >
                  Intră
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ============ SEARCH OVERLAY ============ */}
      {showSearch && (
        <div
          className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-2xl animate-in fade-in duration-200"
          onClick={() => setShowSearch(false)}
          data-testid="search-overlay"
        >
          <div
            className="max-w-2xl mx-auto pt-20 sm:pt-28 px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
              <form onSubmit={handleSearch}>
                <div className="flex items-center gap-3 px-4 border-b border-white/10">
                  <Search className="w-5 h-5 text-slate-500 flex-shrink-0" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Caută anunțuri, categorii, orașe..."
                    autoFocus
                    className="flex-1 h-14 bg-transparent text-white text-base sm:text-lg placeholder:text-slate-600 outline-none"
                    data-testid="search-overlay-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSearch(false)}
                    className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-colors flex-shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </form>

              <div className="p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-3">
                  Categorii populare
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: "auto", name: "Auto", emoji: "🚗" },
                    { id: "imobiliare", name: "Imobiliare", emoji: "🏠" },
                    { id: "electronice", name: "Electronice", emoji: "📱" },
                    { id: "moda", name: "Modă", emoji: "👔" },
                    { id: "locuri-de-munca", name: "Locuri de muncă", emoji: "💼" },
                    { id: "servicii", name: "Servicii", emoji: "🔧" },
                  ].map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.id}`}
                      onClick={() => setShowSearch(false)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/5 hover:bg-blue-500/20 hover:text-blue-300 border border-white/10 hover:border-blue-500/40 text-slate-300 text-sm transition-all"
                    >
                      <span>{cat.emoji}</span>
                      <span>{cat.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ MENU DRAWER (right side desktop, bottom sheet mobile) ============ */}
      {showMenu && (
        <div
          className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-xl animate-in fade-in duration-200"
          onClick={() => setShowMenu(false)}
          data-testid="menu-drawer"
        >
          <div
            className="absolute inset-x-0 bottom-0 sm:inset-y-0 sm:right-0 sm:left-auto sm:w-96 sm:max-w-[90vw] bg-[#0a0a0a] border-t sm:border-t-0 sm:border-l border-white/10 animate-in slide-in-from-bottom sm:slide-in-from-right duration-300 flex flex-col max-h-[92vh] sm:max-h-none sm:h-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10 flex-shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                {user ? (
                  <>
                    {user.picture ? (
                      <img
                        src={user.picture}
                        alt=""
                        className="w-11 h-11 rounded-full object-cover border-2 border-blue-500/50 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-base font-bold">
                          {user.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-white font-semibold truncate text-sm">
                          {user.name}
                        </p>
                        {user.role === "admin" && (
                          <span className="text-[9px] uppercase tracking-wider bg-amber-400/20 text-amber-400 px-1.5 rounded">
                            Admin
                          </span>
                        )}
                        {user.is_verified && (
                          <span
                            className="text-[9px] uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-1.5 rounded flex items-center gap-0.5"
                            title="Identitate verificată"
                            data-testid="menu-verified-chip"
                          >
                            <svg viewBox="0 0 24 24" className="w-2 h-2" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                            Verificat
                          </span>
                        )}
                      </div>
                      <p className="text-slate-500 text-xs truncate">{user.email}</p>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-slate-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Vizitator</p>
                      <Link
                        to="/auth"
                        onClick={() => setShowMenu(false)}
                        className="text-blue-400 text-xs"
                      >
                        Conectează-te →
                      </Link>
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowMenu(false)}
                className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center flex-shrink-0"
                aria-label="Închide meniu"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto p-3 space-y-1">
              {user && (
                <>
                  <MenuLink to="/dashboard" icon={BarChart3} label="Dashboard" onClick={() => setShowMenu(false)} />
                  <MenuLink to="/profile" icon={User} label="Profilul meu" onClick={() => setShowMenu(false)} />
                  <MenuLink to="/favorites" icon={Heart} label="Favorite" onClick={() => setShowMenu(false)} />
                  <MenuLink to="/saved-searches" icon={Bookmark} label="Căutări salvate" onClick={() => setShowMenu(false)} />
                  <MenuLink to="/escrow" icon={Shield} label="Tranzacții Escrow" onClick={() => setShowMenu(false)} />
                  <MenuLink to="/offers" icon={Gift} label="Oferte" onClick={() => setShowMenu(false)} />
                  <MenuLink to="/compare" icon={Scale} label="Compară" onClick={() => setShowMenu(false)} />

                  <MenuSectionTitle>Vânzător</MenuSectionTitle>
                  <MenuLink to="/pricing" icon={Crown} label="Planuri & Abonament" onClick={() => setShowMenu(false)} accent="amber" />
                  <MenuLink to="/seller-dashboard" icon={TrendingUp} label="Statistici vânzător" onClick={() => setShowMenu(false)} />
                  <MenuLink to="/developer/keys" icon={Code2} label="API Keys & Feeds" onClick={() => setShowMenu(false)} accent="indigo" />
                  <MenuLink to="/loyalty" icon={Award} label="Puncte fidelitate" onClick={() => setShowMenu(false)} />
                  <MenuLink to="/referral" icon={Users} label="Program referral" onClick={() => setShowMenu(false)} />

                  {user.role === "admin" && (
                    <>
                      <MenuSectionTitle>Administrare</MenuSectionTitle>
                      <MenuLink to="/admin" icon={Shield} label="Admin Panel" onClick={() => setShowMenu(false)} accent="amber" />
                      <MenuLink to="/admin/disputes" icon={Shield} label="Dispute Escrow" onClick={() => setShowMenu(false)} accent="amber" />
                    </>
                  )}
                </>
              )}

              <MenuSectionTitle>Explorare</MenuSectionTitle>
              {!user && (
                <MenuLink to="/pricing" icon={Crown} label="Planuri & Prețuri" onClick={() => setShowMenu(false)} accent="amber" />
              )}
              <MenuLink to="/auctions" icon={Gavel} label="Arena Licitații" onClick={() => setShowMenu(false)} accent="fuchsia" />
              <MenuLink to="/map" icon={Map} label="Hartă" onClick={() => setShowMenu(false)} />
              <MenuLink to="/blog" icon={Bell} label="Blog" onClick={() => setShowMenu(false)} />
              <MenuLink to="/settings" icon={Settings} label="Setări" onClick={() => setShowMenu(false)} />

              {/* Language Selector */}
              <div className="mt-4 p-3 bg-white/5 rounded-xl">
                <p className="text-slate-500 text-xs mb-2 flex items-center gap-2">
                  <Globe className="w-3.5 h-3.5" />
                  Limbă
                </p>
                <div className="flex gap-1.5">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => setLanguage(lang.code)}
                      className={`flex-1 py-2 rounded-lg flex items-center justify-center gap-1.5 transition-all text-xs ${
                        language === lang.code
                          ? "bg-blue-600 text-white"
                          : "bg-white/5 text-slate-300 hover:bg-white/10"
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {user && (
              <div className="p-3 border-t border-white/10 flex-shrink-0">
                <button
                  onClick={handleLogout}
                  className="w-full h-11 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 font-medium flex items-center justify-center gap-2 hover:bg-red-500/20 transition-colors text-sm"
                  data-testid="nav-logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  Deconectare
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============ BOTTOM FLOATING NAV (all devices) ============ */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-[80]"
        data-testid="unified-bottom-nav"
      >
        <div className="px-2 sm:px-4 pb-2 sm:pb-4">
          <div className="max-w-md mx-auto">
            <div className="relative rounded-2xl sm:rounded-full bg-[#0a0a0a]/85 backdrop-blur-xl border border-white/10 shadow-[0_12px_40px_rgba(0,0,0,0.6)]">
              <div className="pb-safe">
                <div className="flex items-center justify-around h-16 px-1">
                  <BottomNavItem
                    to="/"
                    icon={Home}
                    label="Acasă"
                    isActive={isActive("/")}
                    testId="nav-home"
                  />
                  <BottomNavButton
                    onClick={() => setShowSearch(true)}
                    icon={Search}
                    label="Caută"
                    testId="nav-search-btn"
                  />
                  {/* Center Add Button */}
                  <Link
                    to={user ? "/create-ad" : "/auth"}
                    className="relative -mt-5"
                    data-testid="nav-add-btn"
                    aria-label="Publică anunț"
                  >
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full blur-lg opacity-60" />
                      <div className="relative w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-xl shadow-blue-500/40 transform hover:scale-105 active:scale-95 transition-transform">
                        <PlusCircle className="w-6 h-6 text-white" strokeWidth={2.5} />
                      </div>
                    </div>
                  </Link>
                  <BottomNavItem
                    to={user ? "/messages" : "/auth"}
                    icon={MessageCircle}
                    label="Mesaje"
                    isActive={isActive("/messages")}
                    badge={totalUnread}
                    testId="nav-messages"
                  />
                  <BottomNavButton
                    onClick={() => setShowMenu(true)}
                    icon={Menu}
                    label="Meniu"
                    testId="nav-menu-btn"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}

// ============ Sub-components ============

function BottomNavItem({ to, icon: Icon, label, isActive, badge, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`flex flex-col items-center justify-center w-14 h-14 rounded-xl transition-all ${
        isActive ? "bg-white/10" : ""
      }`}
    >
      <div className="relative">
        <Icon
          className={`w-5 h-5 sm:w-6 sm:h-6 transition-colors ${
            isActive ? "text-blue-400" : "text-slate-400"
          }`}
          strokeWidth={isActive ? 2.5 : 2}
        />
        {badge > 0 && (
          <span className="absolute -top-1 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </div>
      <span
        className={`text-[10px] mt-0.5 transition-colors ${
          isActive ? "text-blue-400 font-medium" : "text-slate-500"
        }`}
      >
        {label}
      </span>
    </Link>
  );
}

function BottomNavButton({ onClick, icon: Icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="flex flex-col items-center justify-center w-14 h-14 rounded-xl hover:bg-white/5 transition-all"
    >
      <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-slate-400" />
      <span className="text-[10px] mt-0.5 text-slate-500">{label}</span>
    </button>
  );
}

function MenuSectionTitle({ children }) {
  return (
    <div className="pt-3 pb-1 px-3">
      <p className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">
        {children}
      </p>
    </div>
  );
}

function MenuLink({ to, icon: Icon, label, badge, onClick, accent }) {
  const accentColor =
    accent === "amber"
      ? "text-amber-400 bg-amber-400/10 group-hover:bg-amber-400/20"
      : accent === "fuchsia"
        ? "text-fuchsia-400 bg-fuchsia-400/10 group-hover:bg-fuchsia-400/20"
        : accent === "indigo"
          ? "text-indigo-400 bg-indigo-400/10 group-hover:bg-indigo-400/20"
          : "text-slate-300 bg-white/5 group-hover:bg-white/10";
  const textColor =
    accent === "amber"
      ? "text-amber-300"
      : accent === "fuchsia"
        ? "text-fuchsia-300"
        : accent === "indigo"
          ? "text-indigo-300"
          : "text-white";
  return (
    <Link
      to={to}
      onClick={onClick}
      className="group flex items-center gap-3 p-2.5 rounded-xl hover:bg-white/5 transition-colors"
    >
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${accentColor}`}
      >
        <Icon className="w-4 h-4" />
      </div>
      <span
        className={`font-medium flex-1 text-sm ${textColor}`}
      >
        {label}
      </span>
      {badge > 0 && (
        <span className="px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold">
          {badge > 9 ? "9+" : badge}
        </span>
      )}
      <ChevronRight className="w-3.5 h-3.5 text-slate-600 group-hover:text-slate-400 transition-colors" />
    </Link>
  );
}
